import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { downloadFile } from '../utils/downloadFile';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

// Form validation and types
interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  webinarDate: string;
  consent: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phone: Yup.string(),
  company: Yup.string(),
  webinarDate: Yup.string().required('Bitte wählen Sie einen Webinar-Termin aus'),
  consent: Yup.boolean()
    .required('Bitte stimmen Sie den Datenschutzbestimmungen zu')
    .oneOf([true], 'Sie müssen den Datenschutzbestimmungen zustimmen, um sich für das Webinar anzumelden'),
});

const initialValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  company: '',
  webinarDate: '',
  consent: false,
};

// API endpoint
const API_ENDPOINT = 'https://3579fdf80154ea27b4d1f7c7d4a13a.57.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/91c4679984b34cde933b6cf2f50f833b/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=2lELbz63PsxeZi8pNnTOImoHQa2bRZ6fPPPiRQky2iw';

// Webinar date options
const WEBINAR_DATES = [
  { value: '2025-10-28', label: '28.10.25 10:00 - 11:30' },
  { value: '2025-10-30', label: '30.10.25 14:00 - 15:30' },
];

export const M365SecurityWebinar = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const headerImage = useHeaderImage(FORM_IDS.M365_SECURITY_WEBINAR);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);
    setSelectedSession(values.webinarDate);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          name: values.name,
          mail: values.email,
          phone: values.phone,
          company: values.company,
          webinarDate: values.webinarDate,
          webinarTime: values.webinarDate === '2025-10-28' ? '10:00 - 11:30 Uhr' : '14:00 - 15:30 Uhr',
          webinarName: 'M365 Tenant Security – Verstehen. Anwenden. Absichern.',
          // SharePoint path where the backend retrieves the ICS file for the confirmation email
          relativeFileUrl: values.webinarDate === '2025-10-28'
            ? '/WebinarICSFiles/2025-10%20M365%20Tenant-Security/M365%20Tenant-Security%202025-10-28.ics'
            : '/WebinarICSFiles/2025-10%20M365%20Tenant-Security/M365%20Tenant-Security%202025-10-30.ics',
          consent: values.consent,
        },
        debugFieldValue: values.name,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (!selectedSession) {
      console.warn('No webinar session selected for download');
      return;
    }
    const originalFileName = selectedSession === '2025-10-28' ? 'webinar-session1.ics' : 'webinar-session2.ics';
    const friendlyFileName = selectedSession === '2025-10-28'
      ? 'M365-Security-Webinar-28-10-2025.ics'
      : 'M365-Security-Webinar-30-10-2025.ics';

    downloadFile({
      fileUrl: `${window.location.origin}/forms/m365securitywebinar/${originalFileName}`,
      fileName: friendlyFileName,
      mimeType: 'text/calendar',
      redirectUrl: 'https://www.lansco.de/',
    });
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="Microsoft 365 Security Webinar" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              Microsoft 365 Security: Was Du wirklich brauchst
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Wir erinnern uns an die gute, alte Zeit zurück, in der der Schutz der eigenen IT hauptsächlich aus Firewalls und Virenscannern bestand. Heute, im Jahr 2025, reicht das längst nicht mehr aus. Unternehmen arbeiten hybrid, Mitarbeitende greifen von überall auf Daten zu, und Angriffe sind professioneller und gezielter denn je. KI-betriebenes Social Engineering macht Phishing-Mails täuschend echt, staatlich gesteuerte Hackergruppen legen ganze Branchen lahm, und automatisierte Angriffsplattformen nutzen Sicherheitslücken schon kurz nach ihrer Veröffentlichung aus.
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              In unserem Webinar erklären wir, was Du in Microsoft 365 unbedingt haben musst, welche Best Practices sich in der Praxis bewährt haben und wie Du typische Fehler vermeidest. Wir zeigen Dir, welche Sicherheitsfunktionen Microsoft 365 von Haus aus bereits mitbringt – wenn man weiß, wo sie stecken – und welche Features in den einzelnen Lizenzpaketen enthalten sind, die Du unbedingt nutzen solltest. Unser Ziel: Dir einen klaren Fahrplan zu geben, wie Du Deinen Tenant sinnvoll, sicher und wirtschaftlich betreibst.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Was Du lernen wirst:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mb: 2, pl: 2, '& li': { mb: 1 } }}>
              <li>Wie Du den Microsoft Secure Score als Startpunkt nutzt und mit Quick Wins sofort Verbesserungen erreichst</li>
              <li>Welche Maßnahmen mit Conditional Access und MFA heute Pflicht sind – ohne Nutzer zu überlasten</li>
              <li>Wie Du mit dedizierten Admin-Konten, Rollenmanagement und PIM Deine sensibelsten Zugänge absicherst</li>
              <li>Welche Lizenzmodelle wirklich Sinn ergeben und wie Du Kostenfallen vermeidest</li>
              <li>Warum Logging, Alerts und Break-Glass-Accounts unverzichtbar sind</li>
              <li>Wie Du mit Best Practices im Betrieb Neuerungen und Probleme frühzeitig erkennst und darauf reagieren kannst</li>
            </Typography>
            {isLoading && (
              <FormLoadingOverlay>
                <CircularProgress size={60} sx={{ color: 'primary.main' }} />
              </FormLoadingOverlay>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnMount={true}
              initialTouched={{
                consent: true
              }}
            >
              {({ errors, touched, isValid, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="name"
                    label="Name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="E-Mail"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="phone"
                    label="Telefonnummer"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Unternehmen"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    margin="normal"
                    name="webinarDate"
                    label="Webinar-Termin auswählen"
                    error={touched.webinarDate && Boolean(errors.webinarDate)}
                    helperText={touched.webinarDate && errors.webinarDate}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  >
                    {WEBINAR_DATES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                  <PrivacyConsentSection
                    label="Ja, ich stimme zu und möchte mich für das Webinar anmelden"
                    isLoading={isLoading}
                  />
                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Für Webinar anmelden'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank für Deine Anmeldung!
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Deine Anmeldung wurde erfolgreich empfangen. Lade Dir jetzt den Kalendertermin herunter, damit Du das Webinar nicht verpasst.
            </Typography>
            <LanscoButton
              // @ts-ignore - Custom variant and HTML anchor props
              variant='white'
              component="a"
              onClick={handleDownload}
              sx={{ minWidth: 220 }}
            >
              Kalendertermin herunterladen
            </LanscoButton>
          </Box>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};